// CustomerRankingAg.tsx
import { useEffect, useRef, useState } from 'react';
import {
  AgGridReact,
  AgGridReactProps,
} from 'ag-grid-react';                           // component + prop types
import { ColumnApi, GridApi } from 'ag-grid-community'; // concrete APIs
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

import moment from 'moment';
import { axiosInstance } from '@/utils/axiosUtil';
import useToken from '@/utils/useToken';
import {
  Button,
  LoadingSpinner,
  Divider,
} from '@data-products-and-ai/react-components';

/* ───────── Row interface for strong typing ───────── */
interface CustomerRow {
  [key: string]: any; // keep generic; list explicit fields if desired
}

const CustomerRankingAg = () => {
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState<CustomerRow[]>([]);
  const getToken = useToken();

  /* refs for the two grid APIs */
  const gridApi = useRef<GridApi | null>(null);
  const columnApi = useRef<ColumnApi | null>(null);

  /* ───────── Column definitions ───────── */
  const colDefs: AgGridReactProps<CustomerRow>['columnDefs'] = [
    { headerName: 'Customer ID', field: 'Customer ID', filter: true },
    { headerName: 'Customer Name', field: 'Customer Name', filter: true },
    { headerName: 'Customer Country', field: 'Customer Country', filter: true },

    {
      headerName: 'Last Active',
      field: 'Last Active',
      filter: 'agDateColumnFilter',
    },
    { headerName: 'Engagement Score', field: 'Engagement Score', filter: true },
    { headerName: 'Machine/Unit ID', field: 'Machine/Unit ID', filter: true },
    { headerName: 'Service Factor', field: 'Service Factor', filter: true },
    {
      headerName: 'Expected Time Frame',
      field: 'Expected Time Frame',
      filter: true,
    },
    { headerName: 'Prediction Driver', field: 'Prediction Driver', filter: true },


    /* auto-height cell */
    {
      headerName: 'Prediction Explanation',
      field: 'Prediction Explanation',
      filter: true,
      flex: 2,
      minWidth: 300,
      autoHeight: true,
      cellStyle: { whiteSpace: 'normal', lineHeight: '1.4em' },
    },

    { headerName: 'Industry PLC', field: 'Industry PLC', filter: true },
    { headerName: 'PCC Responsible', field: 'PCC Responsible', filter: true },
    { headerName: 'RE Responsible', field: 'RE Responsible', filter: true },
    { headerName: 'Parts Support RE', field: 'Parts Support RE', filter: true },
    { headerName: 'Industry', field: 'Industry', filter: true },
    { headerName: 'Industry Segment', field: 'Industry Segment', filter: true },
    {
      headerName: 'Confidence Lower Bound',
      field: 'Confidence Lower Bound',
      filter: true,
    },
    {
      headerName: 'Confidence Upper Bound',
      field: 'Confidence Upper Bound',
      filter: true,
    },
    { headerName: 'Confidence Score', field: 'Confidence Score', filter: true },
    { headerName: 'Revenue (USD)', field: 'Revenue (USD)', filter: true },
    { headerName: 'Frequency', field: 'Frequency', filter: true },
    { headerName: 'Recency', field: 'Recency', filter: true },
    { headerName: 'Profile ID', field: 'PROFILE_ID', filter: true },
  ];

  /* ───────── Data fetch ───────── */
  useEffect(() => {
    (async () => {
      const tk = await getToken();
      if (!tk) return;

      try {
        const res = await axiosInstance.get('/ranking', {
          headers: { Authorization: `Bearer ${tk.accessToken}` },
        });

        const fmtMY = (s: string) =>
          moment(s).isValid() ? moment(s).format('MM/YYYY') : '';

        const mapped: CustomerRow[] = res.data.map((it: any) => ({
          ...it,
          id: it['Customer ID'] + it['Machine/Unit ID'],
          'Last Active': moment(it['Last Active']).toDate(),
          'Expected Time Frame': fmtMY(it['Expected Time Frame']),
          'Confidence Lower Bound': fmtMY(it['Confidence Lower Bound']),
          'Confidence Upper Bound': fmtMY(it['Confidence Upper Bound']),
          'Confidence Score': `${parseFloat(it['Confidence Score']).toFixed(2)}%`,
          'Revenue (USD)': `${parseFloat(it['Revenue (USD)']).toFixed(2)}`,
        }));

        setRowData(mapped);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  /* ───────── Helpers ───────── */
  const onGridReady: AgGridReactProps<CustomerRow>['onGridReady'] = (params) => {
    gridApi.current = params.api;
    columnApi.current = params.columnApi;
  };


  const downloadCsv = () =>
    gridApi.current?.exportDataAsCsv({ fileName: 'customer_ranking.csv' });

  /* ───────── Render ───────── */
  return (
    <div className="ranking__container">
      <Divider />
      {loading ? (
        <LoadingSpinner
          text="Loading..."
          color="#8a00e5"
          backgroundColor="#ADA9C2"
        />
      ) : (
        <>
          <div className="ranking__action" style={{ marginBottom: 8 }}>
                 <Button onClick={downloadCsv}>Download CSV</Button>
          </div>

          <div
            className="ag-theme-quartz"
            style={{ height: 'calc(100vh - 180px)', width: '100%' }}
          >
            <AgGridReact<CustomerRow>
              onGridReady={onGridReady}
              rowData={rowData}
              columnDefs={colDefs}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                wrapText: true,
                floatingFilter:true,
              }}
              floatingFiltersHeight = {34}
              suppressMenuHide={false}
              animateRows
              pagination
              paginationAutoPageSize
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerRankingAg;